import { useCallback } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import { CONNECTIONS_QUERY_KEY } from '@renderer/hooks/integration-query-keys'
import { useUser } from '@renderer/hooks/useUser'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

/** A connect that never opened its browser tab, which the user only sees as a dead click. */
function reportConnectFailed(provider: string | null | undefined, reason: string) {
  AmplitudeRenderer.trackEvent('Connect Failed', { provider: provider ?? undefined, reason })
}

/** The gateway's 5-minute OAuth state lifetime, plus time to come back from the done page. */
const CONNECT_RETURN_WINDOW_MS = 10 * 60 * 1000

/** The done page only deep-links back on a click, so refetch on every return while the connect could still land. */
export function refreshConnectionsOnReturn(
  queryClient: QueryClient,
  appWindow: Pick<Window, 'addEventListener'> = window,
): void {
  const listening = new AbortController()
  appWindow.addEventListener('focus', () => queryClient.invalidateQueries({ queryKey: [CONNECTIONS_QUERY_KEY] }), {
    signal: listening.signal,
  })
  setTimeout(() => listening.abort(), CONNECT_RETURN_WINDOW_MS)
}

export function useConnectProvider() {
  const user = useUser()
  const queryClient = useQueryClient()

  return useCallback(
    async (providerId?: string | null, options?: { connectionId?: string }): Promise<boolean> => {
      if (!user || user.isAnonymous) {
        reportConnectFailed(providerId, user ? 'anonymous' : 'no-user')
        return false
      }

      if (!providerId) {
        reportConnectFailed(providerId, 'no-provider')
        return false
      }

      try {
        const url = await AuthIpcRenderer.getConnectUrlForProvider(providerId, options)
        window.open(url, '_blank')
        refreshConnectionsOnReturn(queryClient)
        return true
      } catch (error) {
        console.error(`Failed to connect provider ${providerId}:`, error)
        // Main's ConnectUrlError carries the reason as `code`; anything else failed in IPC itself.
        reportConnectFailed(providerId, (error as { code?: string }).code || 'ipc-error')
        return false
      }
    },
    [user, queryClient],
  )
}
